//#region src/playback/primitives/error-messages.ts
/**
* Developer-facing copy for what this engine reports.
*
* Everything here reaches a **console**, never a viewer. Viewer-facing copy for a fatal condition is the presentation
* layer's to compose: the engine reports a code, and a consumer maps that code to localized text it owns.
*
* Plain string constants, one export each. Nothing is parameterized — a message that names the engine has to be built
* at the call site from state the engine carries around solely to say it, which is a lot of plumbing for a console
* prefix. Separate exports rather than one object so a composition that logs neither notice doesn't carry their bytes.
*/
/**
* Logged when a fatal `SVTA_UNSUPPORTED_PLAYBACK_FEATURE` is surfaced.
*
* The developer's half of that event; the viewer gets the localized copy the code maps to. One sentence is enough
* because the specifics — which container, which rendition, whether it was DRM — stay structured on the reported
* conditions logged beside it.
*/
const UNSUPPORTED_PLAYBACK_FEATURE_MESSAGE = "Can't play this source: it requires an unsupported playback feature.";
/**
* Logged for **any** fatal condition, by a composition whose failures don't all reduce to a missing feature.
*
* The background variant is the case: it treats a per-rendition cause as fatal on its own, so what reaches its surface
* can be an unsupported container, DRM it can't decrypt, or a source carrying no video at all. Naming one feature would
* be wrong for the last of those, so all three are named broadly. Deferring the _why_ to the conditions instead would
* promise prose they don't carry — an `SvtaError` is a code and its context, and nothing on this path sets `message`.
*/
const UNPLAYABLE_SOURCE_MESSAGE = "Can't play this source — unsupported container, encryption, or no usable video track.";
/**
* LL-HLS delivery, played as standard live. The parser ignores partial segments and the loader fetches whole ones, so
* latency lands wherever `HOLD-BACK` puts it — the stream plays, just not at the latency it was published for.
*/
const LOW_LATENCY_UNSUPPORTED_MESSAGE = "Low-Latency HLS is unsupported; playing as standard live at higher latency.";
/**
* `#EXT-X-PLAYLIST-TYPE:EVENT` — a growing window, which is how DVR is delivered. It plays, but the seekable-range and
* live-edge handling for it is newer and less exercised than sliding-window live.
*/
const DVR_EXPERIMENTAL_MESSAGE = "DVR support for EVENT playlists is experimental.";
//#endregion
export { DVR_EXPERIMENTAL_MESSAGE, LOW_LATENCY_UNSUPPORTED_MESSAGE, UNPLAYABLE_SOURCE_MESSAGE, UNSUPPORTED_PLAYBACK_FEATURE_MESSAGE };
